import { ReactNode, useEffect, useRef, useState } from 'react';

import s from './resizablePane.module.scss';

const keyboardStep = 16;

type ResizablePaneProps = {
  /** The remembered width in pixels, or none to use `defaultWidth`. */
  width: number | undefined;
  defaultWidth: number;
  minWidth: number;
  /**
   * The space to leave the panes beside it, in pixels, when it's wider than
   * its default. It never has to narrow below its default for them.
   */
  minRemainingWidth: number;
  /** Called when a drag ends or a key moves the edge, to remember the width. */
  onWidthChange: (width: number) => void;
  /** Names the drag handle for assistive technology. */
  label: string;
  children: ReactNode;
};

/**
 * A pane whose right edge the user drags to resize it. Double-clicking the
 * edge restores the default width. The width only changes here while
 * dragging, so the children don't rerender with every move.
 */
export function ResizablePane({
  width,
  defaultWidth,
  minWidth,
  minRemainingWidth,
  onWidthChange,
  label,
  children,
}: ResizablePaneProps): JSX.Element {
  const paneRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startWidth: number }>();
  const [dragWidth, setDragWidth] = useState<number>();
  const maxWidth = useMaxWidth(paneRef, defaultWidth, minRemainingWidth);

  const clamp = (value: number) =>
    Math.round(Math.min(Math.max(value, minWidth), maxWidth ?? Infinity));
  const currentWidth = dragWidth ?? width ?? defaultWidth;

  const onPointerDown = (pointerEvent: React.PointerEvent<HTMLDivElement>) => {
    if (pointerEvent.button !== 0 || !paneRef.current) return;
    // Else the drag selects the text it passes over.
    pointerEvent.preventDefault();
    pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId);
    dragRef.current = {
      startX: pointerEvent.clientX,
      startWidth: paneRef.current.getBoundingClientRect().width,
    };
  };

  const onPointerMove = (pointerEvent: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    setDragWidth(clamp(drag.startWidth + pointerEvent.clientX - drag.startX));
  };

  const onPointerUp = () => {
    if (!dragRef.current) return;
    dragRef.current = undefined;
    if (dragWidth !== undefined) onWidthChange(dragWidth);
    setDragWidth(undefined);
  };

  const onKeyDown = (keyEvent: React.KeyboardEvent<HTMLDivElement>) => {
    const shown = clamp(currentWidth);
    const next = {
      ArrowLeft: shown - keyboardStep,
      ArrowRight: shown + keyboardStep,
      Home: minWidth,
      End: maxWidth ?? shown,
    }[keyEvent.key];
    if (next === undefined) return;
    keyEvent.preventDefault();
    onWidthChange(clamp(next));
  };

  return (
    <div
      ref={paneRef}
      className={s.pane}
      style={{
        width: currentWidth,
        minWidth,
        maxWidth: `max(${defaultWidth}px, 100% - ${minRemainingWidth}px)`,
      }}
    >
      {children}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={label}
        aria-valuenow={clamp(currentWidth)}
        aria-valuemin={minWidth}
        aria-valuemax={maxWidth}
        tabIndex={0}
        className={s.handle}
        data-dragging={dragWidth !== undefined || undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={() => onWidthChange(defaultWidth)}
        onKeyDown={onKeyDown}
      />
    </div>
  );
}

/** The widest the pane may grow, following its container's size. */
function useMaxWidth(
  paneRef: React.RefObject<HTMLDivElement>,
  defaultWidth: number,
  minRemainingWidth: number,
): number | undefined {
  const [maxWidth, setMaxWidth] = useState<number>();

  useEffect(() => {
    const container = paneRef.current?.parentElement;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(([entry]) => {
      setMaxWidth(
        Math.max(
          defaultWidth,
          Math.floor(entry.contentRect.width - minRemainingWidth),
        ),
      );
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [paneRef, defaultWidth, minRemainingWidth]);

  return maxWidth;
}
